// Onboarding Types

export type OnboardingStatus = 'in_progress' | 'completed' | 'abandoned';

/**
 * @deprecated Use ThemeMode and Tone for new onboarding flows
 */
export type Personality = 'bold-modern' | 'classic-elegant' | 'warm-friendly' | 'clean-professional';

export type Priority = 'visuals' | 'booking' | 'showcase' | 'trust' | 'standout';

/**
 * Theme mode - simplified visual theme selection
 */
export type ThemeMode = 'light' | 'dark';

/**
 * Tone - how the business wants their content to sound
 * This influences AI content generation
 */
export type Tone =
  | 'witty-playful'        // "We don't take ourselves too seriously"
  | 'strictly-professional' // "Just the facts, no fluff"
  | 'warm-personal'        // "Like talking to a friend"
  | 'bold-confident'       // "We know we're the best"
  | 'calm-reassuring'      // "Everything's going to be OK"
  | 'down-to-earth';       // "Real people, real service"

export interface Step1Data {
  // New simplified fields
  themeMode?: ThemeMode;
  tone?: Tone;
  priorities: Priority[];
  // Legacy field - kept for backward compatibility
  personality?: Personality;
}

export interface Step2Data {
  // V2: Recipe-based style selection
  confirmed_recipe?: string; // The recipe (style) confirmed by the user
  viewed_alternatives?: boolean;
  // Legacy V1 field - kept for backwards compatibility
  selected_template?: string;
}

export interface ExtractedColors {
  primary: string;
  secondary: string;
  palette: string[];
}

export type LogoSource = 'upload' | 'ai-generated';

export interface Step3Data {
  has_logo: boolean;
  logo_url?: string;
  logo_source?: LogoSource;
  primary_color: string;
  secondary_color: string;
  extracted_colors?: ExtractedColors;
}

export interface Step4Data {
  business_description: string;
  tagline?: string;
  services: string;
  years_in_business?: number;
  what_makes_unique?: string; // What makes this business unique/special/different
}

export interface HoursData {
  open: string;
  close: string;
  closed: boolean;
}

export interface Step5Data {
  has_physical_location?: boolean; // Default true; when false, only city/state required
  street_address?: string; // Required only when has_physical_location is true
  city: string;
  state: string;
  zip_code?: string; // Required only when has_physical_location is true
  hours: {
    monday: HoursData;
    tuesday: HoursData;
    wednesday: HoursData;
    thursday: HoursData;
    friday: HoursData;
    saturday: HoursData;
    sunday: HoursData;
  };
}

export type DomainPreference = 'custom' | 'subdomain' | 'purchase';

export interface Step6Data {
  phone?: string;
  email?: string;
  show_email_on_website?: boolean; // Whether to display email publicly on the website
  domain_preference?: DomainPreference; // 'custom' = bring own, 'subdomain' = free niftee.ai, 'purchase' = buy through niftee
  custom_domain?: string; // Only set when domain_preference === 'custom'
  purchased_domain?: string; // Set when domain_preference === 'purchase' and a domain was purchased
  domain_purchase_id?: string; // Purchase record ID from domain_purchases table
  existing_website?: string; // Legacy field — kept for backward compatibility
  scheduling_url?: string; // Calendly, Acuity, OpenTable, etc.
  facebook?: string;
  instagram?: string;
  linkedin?: string;
  youtube?: string;
  yelp?: string;
  google_business?: string;
}

// Step 7: Industry Selection (2-tier) + Industry-specific questions
export interface Step7Data {
  // 2-tier industry selection
  industry_category: string; // Primary category ID (e.g., 'contractor', 'healthcare')
  industry_category_other?: string; // Free-text if "Other" category is selected
  industry_type: string; // Specific type ID (e.g., 'electrician', 'dentist')
  industry_type_other?: string; // Free-text if "Other" type is selected
  // Industry-specific question answers (dynamic based on category/type)
  industry_specific: Record<string, unknown>;
}

// Step 8: Your Story (stats, credentials, testimonials)
export interface BusinessStat {
  label_type: 'years_in_business' | 'years_experience' | 'projects_completed' | 'happy_customers' | 'clients_served' | 'custom';
  custom_label?: string;
  value: number;
  skip?: boolean;
}

export interface Credential {
  name: string;
  issuer?: string;
  license_number?: string;
}

export interface Testimonial {
  quote: string;
  author_name: string;
  rating: number; // 1-5
}

export interface Step8Data {
  stats: BusinessStat[];
  credentials: Credential[];
  testimonials: Testimonial[];
  use_placeholder_testimonials?: boolean;
}

// Step Team: Meet the Team (stored in step_team_data column)
export interface TeamMember {
  id: string; // Unique ID for this team member
  name: string;
  title: string; // e.g., "Owner", "Lead Stylist", "Head Chef"
  bio?: string; // Brief bio (~150 chars)
  photo_url?: string;
  is_owner?: boolean; // Flag for owner/primary contact
  display_order: number; // For sorting
}

export interface StepTeamData {
  team_members: TeamMember[];
  show_team_section: boolean; // Whether to include team on website
}

// Step 9: Gallery & Photos
export type ImageChoice = 'upload' | 'stock' | 'skip';

/**
 * Hero style - determines the hero section layout
 * 'auto' lets niftee AI choose the best layout for the business
 */
export type HeroStyle = 'auto' | 'immersive' | 'full-width' | 'split' | 'compact';

/**
 * Gallery image type - helps Claude understand context for image placement
 */
export type GalleryImageType = 'food-menu' | 'work-examples' | 'our-space' | 'products' | 'other';

export interface GalleryImage {
  url: string;
  caption?: string;
  slot: string; // 'gallery_1', 'gallery_2', etc.
  image_type?: GalleryImageType;
  description?: string; // Brief description (~50 chars)
}

export interface Step9Data {
  hero_style?: HeroStyle;
  hero_image_choice: ImageChoice;
  hero_image_url?: string;
  gallery_choice: ImageChoice;
  gallery_images: GalleryImage[];
}

export interface OnboardingSession {
  id: string;
  customerId: string;
  currentStep: number;
  status: OnboardingStatus;
  source: 'checkout' | 'magic_link' | 'admin';
  createdAt: string;
  updatedAt: string;
  completedAt: string | null;
  // Step data (populated when session is fetched)
  step_1_data?: Step1Data | null;
  step_2_data?: Step2Data | null;
  step_3_data?: Step3Data | null;
  step_4_data?: Step4Data | null;
  step_5_data?: Step5Data | null;
  step_6_data?: Step6Data | null;
  step_7_data?: Step7Data | null;
  step_8_data?: Step8Data | null;
  step_9_data?: Step9Data | null;
  step_team_data?: StepTeamData | null; // Meet the Team (dedicated column)
}

export interface OnboardingCustomer {
  email: string;
  businessName: string;
  industry: string;
  phone: string;
  planTier: 'starter' | 'growth' | 'business';
}

export interface OnboardingStepData {
  step1: Step1Data | null;
  step2: Step2Data | null;
  step3: Step3Data | null;
  step4: Step4Data | null;
  step5: Step5Data | null;
  step6: Step6Data | null;
  step7: Step7Data | null;
  step8: Step8Data | null;
  step9: Step9Data | null;
  stepTeam: StepTeamData | null; // Meet the Team
}

export interface OnboardingSessionResponse {
  session: OnboardingSession;
  customer: OnboardingCustomer;
  stepData: OnboardingStepData;
}

export interface TemplateRecommendation {
  recommended: string;
  alternatives: string[];
  industry: string;
  basedOn: {
    personality: string;
    priorities: string[];
  };
}

export interface OnboardingByCustomerResponse {
  sessionId: string;
  status: OnboardingStatus;
  currentStep: number;
  redirectUrl: string;
}

// Step configuration
export interface StepConfig {
  number: number;
  title: string;
  description: string;
}

/**
 * Optimized onboarding step order (12 steps):
 * 1. Industry first - determines sections, labels, CTAs, questions
 * 2. Business details - core identity + what makes you unique
 * 3. Brand assets - logo + colors
 * 4. Theme & tone - visual + voice (with colors for preview)
 * 5-6. Location & contact info
 * 7. Trust builders (stats, credentials, testimonials)
 * 8. Meet the Team - owner/staff bios and photos (trust + credibility)
 * 9. Gallery - with full context for image selection
 * 10. Site preview - see it all together
 * 11. Review - final data check
 * 12. AI Profile - brand summary approval
 */
export const STEPS: StepConfig[] = [
  { number: 1, title: 'Industry', description: 'What type of business are you?' },
  { number: 2, title: 'Business Details', description: 'Tell us about what you do' },
  { number: 3, title: 'Brand Assets', description: 'Logo and colors' },
  { number: 4, title: 'Theme & Tone', description: 'Your look and voice' },
  { number: 5, title: 'Location & Hours', description: 'Where and when' },
  { number: 6, title: 'Social & Contact', description: 'How to reach you' },
  { number: 7, title: 'Your Story', description: 'Stats, credentials, and testimonials' },
  { number: 8, title: 'Meet the Team', description: 'Introduce your team' },
  { number: 9, title: 'Gallery & Photos', description: 'Hero image and photo gallery' },
  { number: 10, title: 'Review', description: 'Verify your information' },
  { number: 11, title: 'Brand Profile', description: 'AI-generated brand summary' },
];

// Personality options
export const PERSONALITY_OPTIONS: Array<{
  value: Personality;
  label: string;
  description: string;
  icon: string;
}> = [
  {
    value: 'bold-modern',
    label: 'Bold & Modern',
    description: 'Strong visuals, contemporary design, makes a statement',
    icon: 'Zap',
  },
  {
    value: 'classic-elegant',
    label: 'Classic & Elegant',
    description: 'Timeless design, refined typography, sophisticated feel',
    icon: 'Crown',
  },
  {
    value: 'warm-friendly',
    label: 'Warm & Friendly',
    description: 'Inviting colors, approachable tone, personal touch',
    icon: 'Heart',
  },
  {
    value: 'clean-professional',
    label: 'Clean & Professional',
    description: 'Minimal design, clear information, trustworthy appearance',
    icon: 'Briefcase',
  },
];

// Priority options
export const PRIORITY_OPTIONS: Array<{
  value: Priority;
  label: string;
  description: string;
}> = [
  { value: 'visuals', label: 'Stunning Visuals', description: 'Showcase beautiful imagery' },
  { value: 'booking', label: 'Easy Booking', description: 'Get appointments/reservations' },
  { value: 'showcase', label: 'Showcase Services', description: 'Display what you offer' },
  { value: 'trust', label: 'Build Trust', description: 'Establish credibility' },
  { value: 'standout', label: 'Stand Out', description: 'Differentiate from competitors' },
];

// Theme mode options
export const THEME_MODE_OPTIONS: Array<{
  value: ThemeMode;
  label: string;
  description: string;
}> = [
  {
    value: 'light',
    label: 'Light',
    description: 'Clean, bright backgrounds with dark text',
  },
  {
    value: 'dark',
    label: 'Dark',
    description: 'Bold, dark backgrounds with light text',
  },
];

// Tone options - influences AI content generation
export const TONE_OPTIONS: Array<{
  value: Tone;
  label: string;
  tagline: string;
  description: string;
  icon: string;
}> = [
  {
    value: 'witty-playful',
    label: 'Witty & Playful',
    tagline: "We don't take ourselves too seriously",
    description: 'Light humor, clever wordplay, and a fun personality that stands out',
    icon: 'Sparkles',
  },
  {
    value: 'strictly-professional',
    label: 'Strictly Professional',
    tagline: 'Just the facts, no fluff',
    description: 'Direct, authoritative, and focused on expertise and results',
    icon: 'Briefcase',
  },
  {
    value: 'warm-personal',
    label: 'Warm & Personal',
    tagline: 'Like talking to a friend',
    description: 'Conversational, caring, and focused on building relationships',
    icon: 'Heart',
  },
  {
    value: 'bold-confident',
    label: 'Bold & Confident',
    tagline: "We know we're the best",
    description: 'Strong statements, clear value propositions, unapologetic excellence',
    icon: 'Zap',
  },
  {
    value: 'calm-reassuring',
    label: 'Calm & Reassuring',
    tagline: "Everything's going to be OK",
    description: 'Soothing, trustworthy, and focused on alleviating concerns',
    icon: 'Shield',
  },
  {
    value: 'down-to-earth',
    label: 'Down to Earth',
    tagline: 'Real people, real service',
    description: 'Honest, straightforward, no-nonsense approach',
    icon: 'Users',
  },
];

// Template metadata for previews
export const TEMPLATE_METADATA: Record<string, {
  name: string;
  description: string;
  previewUrl: string;
  features: string[];
}> = {
  'bold-showcase': {
    name: 'Bold Showcase',
    description: 'Modern restaurant template with stunning visuals and animated menu highlights',
    previewUrl: '/templates/bold-showcase/preview.png',
    features: ['Time-aware hours', 'Animated menu items', 'Gallery with hover effects'],
  },
  'elegant-fine-dining': {
    name: 'Elegant Fine Dining',
    description: 'Sophisticated template for upscale dining experiences',
    previewUrl: '/templates/elegant-fine-dining/preview.png',
    features: ['Clean layout', 'Reservation focus', 'Menu sections'],
  },
  'sophisticated-results': {
    name: 'Sophisticated Results',
    description: 'Professional services template emphasizing expertise and results',
    previewUrl: '/templates/sophisticated-results/preview.png',
    features: ['Stats counters', 'Team section', 'Case studies'],
  },
  'professional-expertise': {
    name: 'Professional Expertise',
    description: 'Clean professional template highlighting credentials and services',
    previewUrl: '/templates/professional-expertise/preview.png',
    features: ['Service cards', 'Credentials display', 'Contact focus'],
  },
  'premium-portfolio': {
    name: 'Premium Portfolio',
    description: 'Contractor template showcasing work with before/after galleries',
    previewUrl: '/templates/premium-portfolio/preview.png',
    features: ['Project gallery', 'Before/after slider', 'Service areas'],
  },
  'trustworthy-credentials': {
    name: 'Trustworthy Credentials',
    description: 'Trust-focused template for contractors highlighting licenses and reviews',
    previewUrl: '/templates/trustworthy-credentials/preview.png',
    features: ['License display', 'Review highlights', 'Service list'],
  },
  'welcoming-dental': {
    name: 'Welcoming Dental',
    description: 'Friendly healthcare template designed for dental practices',
    previewUrl: '/templates/welcoming-dental/preview.png',
    features: ['Provider profiles', 'Service cards', 'Patient forms'],
  },
  'clean-appointments': {
    name: 'Clean Appointments',
    description: 'Modern healthcare template focused on easy appointment booking',
    previewUrl: '/templates/clean-appointments/preview.png',
    features: ['Booking integration', 'Provider info', 'Insurance section'],
  },
};

// Hero style options for V3 generation
// User-friendly labels that avoid technical "hero" terminology
export const HERO_STYLE_OPTIONS: Array<{
  value: HeroStyle;
  label: string;
  description: string;
  previewClass: string; // Tailwind classes for visual preview
}> = [
  {
    value: 'auto',
    label: 'Let niftee decide (Recommended)',
    description: "We'll pick the best layout for your business and industry",
    previewClass: '',
  },
  {
    value: 'split',
    label: 'Side by Side',
    description: 'Text on one side, image on the other - clean and balanced',
    previewClass: 'grid grid-cols-2',
  },
  {
    value: 'full-width',
    label: 'Full Width Banner',
    description: 'Full-width background image with text overlay - bold and impactful',
    previewClass: 'bg-cover bg-center',
  },
  {
    value: 'immersive',
    label: 'Full Screen',
    description: 'Image fills the entire screen - dramatic, magazine-style',
    previewClass: 'bg-cover bg-center h-screen',
  },
  {
    value: 'compact',
    label: 'Minimal',
    description: 'Shorter top section, less imagery - content-focused',
    previewClass: 'py-12',
  },
];

// Gallery image type options - helps Claude understand image context
export const GALLERY_IMAGE_TYPE_OPTIONS: Array<{
  value: GalleryImageType;
  label: string;
  description: string;
}> = [
  {
    value: 'food-menu',
    label: 'Food / Menu',
    description: 'Dishes, menu items, food photography',
  },
  {
    value: 'work-examples',
    label: 'Work Examples',
    description: 'Before/after, completed projects, results',
  },
  {
    value: 'our-space',
    label: 'Our Space',
    description: 'Interior, exterior, workspace photos',
  },
  {
    value: 'products',
    label: 'Products',
    description: 'Product shots, merchandise, inventory',
  },
  {
    value: 'other',
    label: 'Other',
    description: 'Team photos, events, misc images',
  },
];

// Recipe metadata for V2 style previews
// Maps personality choices to visual style information
export const RECIPE_METADATA: Record<Personality, {
  label: string;
  tagline: string;
  description: string;
  features: string[];
  accentColor: string;
  previewUrl: string;
  previewUrlMobile: string;
  previewUrlThumb: string;
}> = {
  'bold-modern': {
    label: 'Bold & Modern',
    tagline: 'Make a statement with striking design',
    description: 'Dark backgrounds, bold typography, high contrast, and dynamic animations create an impactful first impression.',
    features: ['Dark mode aesthetic', 'Bold typography', 'High contrast', 'Smooth animations', 'Statement hero'],
    accentColor: '#3AABE8',
    previewUrl: '/recipe-previews/bold-modern-desktop.png',
    previewUrlMobile: '/recipe-previews/bold-modern-mobile.png',
    previewUrlThumb: '/recipe-previews/bold-modern-thumb.png',
  },
  'classic-elegant': {
    label: 'Classic & Elegant',
    tagline: 'Timeless sophistication',
    description: 'Refined serif typography, muted color palettes, and generous whitespace convey established professionalism.',
    features: ['Serif typography', 'Refined color palette', 'Elegant spacing', 'Subtle animations', 'Premium feel'],
    accentColor: '#8B7355',
    previewUrl: '/recipe-previews/classic-elegant-desktop.png',
    previewUrlMobile: '/recipe-previews/classic-elegant-mobile.png',
    previewUrlThumb: '/recipe-previews/classic-elegant-thumb.png',
  },
  'warm-friendly': {
    label: 'Warm & Friendly',
    tagline: 'Approachable and inviting',
    description: 'Warm colors, rounded shapes, and friendly typography create an approachable, personal feel.',
    features: ['Warm color palette', 'Rounded corners', 'Friendly typography', 'Inviting imagery', 'Personal touch'],
    accentColor: '#E8913A',
    previewUrl: '/recipe-previews/warm-friendly-desktop.png',
    previewUrlMobile: '/recipe-previews/warm-friendly-mobile.png',
    previewUrlThumb: '/recipe-previews/warm-friendly-thumb.png',
  },
  'clean-professional': {
    label: 'Clean & Professional',
    tagline: 'Clear, trustworthy, efficient',
    description: 'Minimal design with clear hierarchy, ample whitespace, and crisp typography builds trust and credibility.',
    features: ['Minimal design', 'Clear hierarchy', 'Crisp typography', 'Generous whitespace', 'Trust signals'],
    accentColor: '#2563EB',
    previewUrl: '/recipe-previews/clean-professional-desktop.png',
    previewUrlMobile: '/recipe-previews/clean-professional-mobile.png',
    previewUrlThumb: '/recipe-previews/clean-professional-thumb.png',
  },
};
